/**
 * Phase C implementation — click-to-edit text directly in the preview iframe.
 *
 * ADR-002 (`thread/docs/deck-editor/ADR-002-direct-manipulation-editing.md`).
 *
 * The preview is a **same-origin** server-rendered iframe at
 * `/api/decks/{id}/render`, so the parent can reach `iframe.contentWindow`,
 * the live DOM, and the runtime's `window.__threadPresentation`. This hook
 * makes individual text nodes editable in place:
 *
 *   1. The renderer emits `data-edit-path="<path>"` on each editable text
 *      run inside `bodyHtml` (the DOM ↔ bodyHtml source map — see
 *      `./README.md` for the exact contract the renderer must satisfy).
 *   2. On click, the target editable element becomes `contentEditable`.
 *   3. On blur (or Enter), we read the changed inner HTML back and call
 *      `onSave(editPath, newHtml)` — the caller persists it by splicing
 *      `newHtml` into the slide's `bodyHtml` at `editPath` and issuing the
 *      slide PUT (which bumps `cacheTag` → the iframe reloads).
 *
 * The runtime re-renders the slide DOM on every navigation and fires a
 * `thread-slide-rendered` CustomEvent (`detail: { slideIdx, root }`,
 * runtime `renderSlideContent`). Handlers therefore **re-attach on each
 * render**, not once — we listen for that event (plus iframe `load`) and
 * (re)wire the editable nodes in the freshly-mounted `root`.
 *
 * `DeckEditor` wires `onSave` through `editPathSplice` and the shared slide
 * autosave writer. The callback keeps this hook independent of persistence
 * while the live editor integration supplies the durable write.
 */

import { useCallback, useEffect, useRef } from "react";

import { EDIT_KIND_ATTR, EDIT_KIND_TEXT } from "./editPathMap";

/**
 * Attribute the renderer stamps onto each editable text run. Its value is
 * the `editPath` — an opaque, stable address into the slide's `bodyHtml`
 * (see `./README.md` → "the `data-edit-path` contract").
 */
export const EDIT_PATH_ATTR = "data-edit-path";

/**
 * CSS selector matching every **editable text leaf** in a rendered slide.
 * Phase C only makes `data-edit-kind="text"` runs `contentEditable`; block
 * containers (`data-edit-kind="block"`) are draggable (Phase D,
 * `useBlockDnd`) but must NOT become editable, or a click on a section
 * wrapper would turn the whole block into a text field.
 */
export const EDITABLE_SELECTOR = `[${EDIT_PATH_ATTR}][${EDIT_KIND_ATTR}="${EDIT_KIND_TEXT}"]`;

/**
 * The runtime fires this on the parent `window` (inside the iframe) after
 * each slide mount, carrying the freshly-rendered `root`. We re-attach our
 * editing affordances on every occurrence so handlers survive re-renders.
 */
export const SLIDE_RENDERED_EVENT = "thread-slide-rendered";

/** Marker class added to a node while it is the active editable target. */
export const EDITING_CLASS = "thread-inplace-editing";

/**
 * Per-slide marker the plain ``edit`` layout stamps on each slide's wrapper
 * (``<section data-slide-idx="N">``), mirroring the mobile DOM's
 * ``.m-card[data-slide-idx]``.
 */
export const SLIDE_IDX_ATTR = "data-slide-idx";

/**
 * Which slide a node belongs to, from its containing ``[data-slide-idx]``
 * section. The plain ``edit`` layout (FXC-8818) lays ALL slides out at once,
 * each in its own ``<section data-slide-idx>``, so the runtime's single-slide
 * ``currentSlide`` is no longer the source of truth for where an edit lands —
 * the clicked run's own section is. Returns null when there is no such
 * ancestor (the 3D / single-slide layouts), where the caller falls back to the
 * runtime's tracked slide index.
 */
export function closestSlideIdx(el: HTMLElement): number | null {
  const section = el.closest<HTMLElement>(`[${SLIDE_IDX_ATTR}]`);
  const raw = section?.getAttribute(SLIDE_IDX_ATTR);
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/** Shape of the `thread-slide-rendered` CustomEvent detail. */
interface SlideRenderedDetail {
  slideIdx: number;
  root: HTMLElement;
}

/** A single in-place edit, ready to be spliced back into `bodyHtml`. */
export interface InPlaceEdit {
  /** The `data-edit-path` value of the node that changed. */
  editPath: string;
  /**
   * The node's new **text content** (`el.textContent`), after the user's
   * edit. This is the persistence unit (README §3): the live DOM under an
   * edit-path node carries runtime-injected word spans / fragment attrs, so
   * we splice the user's plain text into `bodyHtml` at `editPath` rather
   * than its `innerHTML`. Whitespace is collapsed/trimmed to drop runtime
   * cruft.
   */
  text: string;
  /**
   * The node's new inner HTML, after the user's edit. Kept for callers that
   * opt into HTML-level diffing once the renderer guarantees clean inline
   * markup inside a run (a documented follow-up). The current persistence
   * path uses `text`, not `newHtml`.
   */
  newHtml: string;
  /** The node's previous text content (for diffing / no-op suppression). */
  prevText: string;
  /** The node's previous inner HTML (captured at edit-start). */
  prevHtml: string;
  /** Slide index the edit happened on, if known from the last render. */
  slideIdx: number | null;
}

/** Persist callback — the caller splices `newHtml` into `bodyHtml`. */
export type OnSaveEdit = (edit: InPlaceEdit) => void;

export interface UseInPlaceEditOptions {
  /**
   * The same-origin preview iframe. Pass a ref/getter rather than the
   * element so the hook can re-query `contentWindow`/`contentDocument`
   * after the `cacheTag`-driven reload swaps the document underneath it.
   */
  getIframe: () => HTMLIFrameElement | null;
  /** Called on blur/commit with the changed run. Stub persistence here. */
  onSave: OnSaveEdit;
  /** Master switch — when false, the hook attaches nothing (default true). */
  enabled?: boolean;
  /**
   * Optional hook fired when a node enters edit mode (for toolbars, focus
   * rings, telemetry). Receives the live element + its editPath.
   */
  onEditStart?: (el: HTMLElement, editPath: string) => void;
}

export interface UseInPlaceEditApi {
  /**
   * Force a re-attach pass against the current slide root. Normally
   * unnecessary (we listen for `thread-slide-rendered`), but exposed for
   * the integration to call after it knows a fresh render has landed.
   */
  reattach(): void;
  /** True while a node is actively being edited (contentEditable focused). */
  isEditing(): boolean;
}

/**
 * Read the editable content of a node. The runtime post-processes a slide
 * after `root.innerHTML = bodyHtml` — `wrapHtmlWords` wraps text in
 * `<span data-dag-word>` and `assignFragmentKeys` stamps `data-fragment-*`.
 * So the live DOM under an edit-path node is NOT the pristine bodyHtml. We
 * therefore read the node's effective text and let the caller decide how to
 * reconcile it with `bodyHtml` (the spec's recommendation: edit-path nodes
 * are *leaf text runs*, so `textContent` is the safe round-trip unit; richer
 * inline markup is a follow-up). `innerHTML` is captured too for callers
 * that opt into HTML-level diffing once the renderer guarantees clean runs.
 */
function readEditableHtml(el: HTMLElement): { text: string; html: string } {
  return { text: sanitizeText(el.textContent ?? ""), html: el.innerHTML };
}

/**
 * Trim the runtime cruft off a committed text run. The runtime wraps words
 * in `<span data-dag-word>`, which `textContent` flattens back to plain
 * characters, but the wrapping (and `contentEditable` itself) can leave
 * stray whitespace — collapse internal runs of whitespace to single spaces
 * and trim the ends. This is the "good enough" normalization the spec calls
 * for (README §3 / §7); rich inline markup inside one run is a follow-up.
 */
function sanitizeText(raw: string): string {
  return raw.replace(/\s+/g, " ").trim();
}

export function useInPlaceEdit(
  options: UseInPlaceEditOptions,
): UseInPlaceEditApi {
  const { getIframe, onSave, enabled = true, onEditStart } = options;

  // Keep the latest callbacks in refs so the long-lived listeners we attach
  // to the iframe document don't need to be torn down + re-bound on every
  // parent re-render (which would race the runtime's own re-renders).
  const onSaveRef = useRef<OnSaveEdit>(onSave);
  const onEditStartRef = useRef<UseInPlaceEditOptions["onEditStart"]>(onEditStart);
  onSaveRef.current = onSave;
  onEditStartRef.current = onEditStart;

  const lastSlideIdxRef = useRef<number | null>(null);
  const editingElRef = useRef<HTMLElement | null>(null);
  const editingSlideIdxRef = useRef<number | null>(null);
  // Snapshot of the node's text + HTML at edit-start, to diff against on
  // commit. `text` drives no-op suppression + persistence (README §3);
  // `html` is kept to restore the node verbatim on Escape (cancel).
  const editingPrevTextRef = useRef<string>("");
  const editingPrevHtmlRef = useRef<string>("");

  const commitEdit = useCallback(() => {
    const el = editingElRef.current;
    if (!el) return;
    const editPath = el.getAttribute(EDIT_PATH_ATTR);
    el.removeAttribute("contenteditable");
    el.classList.remove(EDITING_CLASS);
    editingElRef.current = null;
    const prevText = editingPrevTextRef.current;
    const prevHtml = editingPrevHtmlRef.current;
    editingPrevTextRef.current = "";
    editingPrevHtmlRef.current = "";
    const slideIdx = editingSlideIdxRef.current;
    editingSlideIdxRef.current = null;
    if (!editPath) return;
    const { text, html: newHtml } = readEditableHtml(el);
    // No-op suppression is on the **text** (the persistence unit) — the
    // runtime mutates `innerHTML` after mount (word spans), so diffing HTML
    // would fire spurious saves on every focus even when the copy is
    // unchanged.
    if (text === prevText) return;
    onSaveRef.current({
      editPath,
      text,
      newHtml,
      prevText,
      prevHtml,
      slideIdx,
    });
  }, []);

  const beginEdit = useCallback(
    (el: HTMLElement) => {
      if (editingElRef.current === el) return;
      // Commit any in-flight edit before switching targets.
      if (editingElRef.current) commitEdit();
      const editPath = el.getAttribute(EDIT_PATH_ATTR);
      if (!editPath) return;
      const snapshot = readEditableHtml(el);
      editingPrevTextRef.current = snapshot.text;
      editingPrevHtmlRef.current = snapshot.html;
      // Prefer the clicked run's own ``[data-slide-idx]`` section (the plain
      // ``edit`` layout, where all slides are visible at once — FXC-8818); fall
      // back to the runtime's tracked slide for the single-slide 3D layout.
      editingSlideIdxRef.current =
        closestSlideIdx(el) ?? lastSlideIdxRef.current;
      editingElRef.current = el;
      el.setAttribute("contenteditable", "true");
      el.classList.add(EDITING_CLASS);
      el.focus();
      onEditStartRef.current?.(el, editPath);
    },
    [commitEdit],
  );

  // Delegated handlers live at the document level so they keep working as
  // the runtime re-renders slide DOM (the editable nodes are replaced, but
  // the document — and thus these listeners — persists across navigations).
  const onDocClick = useCallback(
    (ev: Event) => {
      const target = ev.target as HTMLElement | null;
      const editable = target?.closest<HTMLElement>(EDITABLE_SELECTOR);
      if (!editable) return;
      beginEdit(editable);
    },
    [beginEdit],
  );

  const onDocBlur = useCallback(
    (ev: FocusEvent) => {
      if (ev.target === editingElRef.current) commitEdit();
    },
    [commitEdit],
  );

  const onDocKeyDown = useCallback(
    (ev: KeyboardEvent) => {
      if (!editingElRef.current) return;
      if (ev.key === "Enter" && !ev.shiftKey) {
        ev.preventDefault();
        editingElRef.current.blur(); // → onDocBlur → commitEdit
      } else if (ev.key === "Escape") {
        // Abandon the edit: restore the snapshot, don't save.
        ev.preventDefault();
        const el = editingElRef.current;
        el.innerHTML = editingPrevHtmlRef.current;
        editingPrevTextRef.current = "";
        editingPrevHtmlRef.current = "";
        editingSlideIdxRef.current = null;
        editingElRef.current = null;
        el.removeAttribute("contenteditable");
        el.classList.remove(EDITING_CLASS);
      }
    },
    [],
  );

  const onSlideRendered = useCallback((ev: Event) => {
    const detail = (ev as CustomEvent<SlideRenderedDetail>).detail;
    if (detail && typeof detail.slideIdx === "number") {
      lastSlideIdxRef.current = detail.slideIdx;
    }
    // Editable nodes carry their behaviour via document-level delegation, so
    // there is nothing per-node to re-bind here. We keep the hook so the
    // integration can, in a follow-up, add affordances (hover outlines, an
    // "editable" cursor) onto the fresh `detail.root` on every render.
  }, []);

  const reattach = useCallback(() => {
    const iframe = getIframe();
    const win = iframe?.contentWindow;
    if (win) {
      lastSlideIdxRef.current =
        (win as unknown as { __threadPresentation?: { currentSlide?: number } })
          .__threadPresentation?.currentSlide ?? lastSlideIdxRef.current;
    }
  }, [getIframe]);

  useEffect(() => {
    if (!enabled) return;
    const iframe = getIframe();
    const win = iframe?.contentWindow;
    const doc = iframe?.contentDocument ?? win?.document ?? null;
    if (!win || !doc) return;

    // Delegated, capture-phase listeners on the iframe document. Capture so
    // we see the click before the runtime's own keyboard/click handlers
    // (which `stopImmediatePropagation` for fragment nav) swallow it.
    doc.addEventListener("click", onDocClick, true);
    doc.addEventListener("blur", onDocBlur, true);
    doc.addEventListener("keydown", onDocKeyDown, true);
    win.addEventListener(SLIDE_RENDERED_EVENT, onSlideRendered);

    // The canonical thread-v5-3d runtime sets ``pointer-events: none`` on slide
    // text so the 3D substrate <canvas> receives pointer events. That means a
    // real mouse click passes THROUGH an editable run and lands on the canvas —
    // so without this, clicking text in the preview does nothing. In edit mode
    // we inject a stylesheet that (a) re-enables pointer events on the editable
    // runs so clicks reach them, and (b) adds hover + active-edit affordances so
    // it's visible what can be edited and that you're editing. It lives in
    // <head> so it survives the runtime's per-slide DOM re-renders, and is
    // removed when edit mode turns off / the iframe reloads.
    const STYLE_ID = "thread-inplace-affordances";
    if (!doc.getElementById(STYLE_ID)) {
      const style = doc.createElement("style");
      style.id = STYLE_ID;
      style.textContent = [
        '[data-edit-path]{pointer-events:auto !important;}',
        '[data-edit-path][data-edit-kind="text"]{cursor:text;}',
        '[data-edit-path][data-edit-kind="text"]:hover{outline:1px dashed rgba(26,175,107,.75);outline-offset:2px;border-radius:2px;}',
        '[data-edit-path][data-edit-kind="block"]:hover{outline:1px dashed rgba(26,175,107,.35);outline-offset:3px;}',
        '[data-edit-path].' + EDITING_CLASS + '{outline:2px solid #1aaf6b !important;outline-offset:2px;background:rgba(26,175,107,.07);cursor:text;}',
      ].join("\n");
      doc.head.appendChild(style);
    }

    // Seed the current slide index in case we attached after first render.
    reattach();

    return () => {
      doc.removeEventListener("click", onDocClick, true);
      doc.removeEventListener("blur", onDocBlur, true);
      doc.removeEventListener("keydown", onDocKeyDown, true);
      win.removeEventListener(SLIDE_RENDERED_EVENT, onSlideRendered);
      doc.getElementById(STYLE_ID)?.remove();
      // Commit a dangling edit if the iframe is being torn down (the
      // cacheTag reload swaps the document, so the blur may never fire).
      if (editingElRef.current) commitEdit();
    };
    // Re-run when the iframe identity could change (reload). The caller is
    // expected to thread a key/cacheTag-derived dependency through here in
    // the integration; in isolation we depend on the stable callbacks.
  }, [
    enabled,
    getIframe,
    onDocClick,
    onDocBlur,
    onDocKeyDown,
    onSlideRendered,
    reattach,
    commitEdit,
  ]);

  return {
    reattach,
    isEditing: () => editingElRef.current !== null,
  };
}
